// In-App designer rules shared by the editor and the message list: per-surface config, dismiss
// paths, contrast, and the publish gate. Pure (no React, no network) so the editor's Publish,
// its Save of a live message and the list-row Publish all apply the SAME rules (P-4 / F29 / F15).
// `validateMessageDoc` reads the stored authoring doc — the shape the editor PUTs and
// GET /in-app-messages/:id returns ({ message_type, design.pages[].blocks|style, trigger, … }).

import { urlProblem } from "./inAppUrls";
import { worstCaseText, mergeTagKeys } from "./inAppMergeTags";
import { effectiveAppearance, darkPreviewBlocks, darkPreviewSurface, cssOf, Brand } from "./inAppDark";

export type SurfaceType = "modal" | "banner" | "slide-in" | "fullscreen";

export interface RuleBlock {
  id?: string;
  type: string;
  content: Record<string, any>;
}

export interface RuleStyle {
  background: string;
  closeButton: boolean;
  tapBackdrop: boolean;
  swipeToDismiss: boolean;
  autoDismissSecs: number;
  animation: string;
  position: string;
  [k: string]: any;
}

// ─── Per-surface configuration ────────────────────────────────────────────────
// One object drives every surface-specific behaviour: which blocks are allowed,
// which Style controls render, and which dismiss paths exist. This is the same
// config the SDK teams consume — BUG-03 / BUG-04 / BUG-02 are all one source.

export interface SurfaceConfig {
  allowedBlocks: string[];
  style: {
    cornerRadius: boolean;
    backdropOpacity: boolean;
    width: boolean;                              // exposes a width control
    solidBackgroundOnly: boolean;                // banner: no gradient/image bg
    animations: { value: string; label: string }[];
    positions: { value: string; label: string }[]; // empty = no position control
  };
  dismiss: {
    closeButton: "required" | "optional" | "none";
    tapBackdrop: boolean;
    swipe: boolean;
    autoDismiss: "default" | "optional" | "none";
  };
}

export const ALL_BLOCKS: string[] = [
  "heading", "text", "image", "button", "icon", "feature-list",
  "container", "row", "columns", "divider", "spacer",
  "countdown", "coupon", "product",
  "carousel", "rating", "choice", "input", "consent", "progress", "video",
];

export const SURFACE_CONFIG: Record<SurfaceType, SurfaceConfig> = {
  modal: {
    allowedBlocks: [...ALL_BLOCKS, "form"],
    style: {
      cornerRadius: true, backdropOpacity: true, width: true, solidBackgroundOnly: false,
      // Entry animation (Figma): Fade + scale (default) / Slide up / Slide down / None
      animations: [{ value: "fade-scale", label: "Fade + scale (default)" }, { value: "slide-up", label: "Slide up" }, { value: "slide-down", label: "Slide down" }, { value: "none", label: "None" }],
      positions: [],  // modal: no message-position; the close-button Position is shown instead
    },
    dismiss: { closeButton: "optional", tapBackdrop: false, swipe: false, autoDismiss: "none" },
  },
  banner: {
    allowedBlocks: ["heading", "text", "image", "button", "divider", "spacer"], // Gap 12: six basic blocks
    style: {
      cornerRadius: false, backdropOpacity: false, width: false, solidBackgroundOnly: true,
      animations: [{ value: "slide-up", label: "Slide" }],
      positions: [{ value: "top", label: "Top" }, { value: "bottom", label: "Bottom" }],
    },
    dismiss: { closeButton: "optional", tapBackdrop: false, swipe: true, autoDismiss: "default" },
  },
  "slide-in": {
    allowedBlocks: ALL_BLOCKS,
    style: {
      cornerRadius: true, backdropOpacity: false, width: true, solidBackgroundOnly: false,
      animations: [{ value: "slide-up", label: "Slide" }],
      positions: [
        { value: "bottom-right", label: "Bottom right" }, { value: "bottom-left", label: "Bottom left" },
        { value: "top-right", label: "Top right" }, { value: "top-left", label: "Top left" },
      ],
    },
    dismiss: { closeButton: "optional", tapBackdrop: false, swipe: true, autoDismiss: "optional" },
  },
  fullscreen: {
    allowedBlocks: [...ALL_BLOCKS, "form"],
    style: {
      cornerRadius: false, backdropOpacity: false, width: false, solidBackgroundOnly: false,
      animations: [{ value: "fade", label: "Fade" }, { value: "slide-up", label: "Slide" }],
      positions: [],
    },
    dismiss: { closeButton: "required", tapBackdrop: false, swipe: false, autoDismiss: "none" },
  },
};

// Banner slot character limits (Part E1) — hard caps enforced in the UI.
export const BANNER_LIMITS = { heading: 30, text: 35, button: 12 };

// Coerce a style object onto a surface's allowed animation/position set so a
// Chakra/native <select> never renders blank on a value it doesn't offer (ANTI-12).
export function coerceStyleForSurface<S extends RuleStyle>(style: S, type: SurfaceType): S {
  const cfg = SURFACE_CONFIG[type];
  const next = { ...style };
  if (!cfg.style.animations.some(a => a.value === next.animation)) next.animation = cfg.style.animations[0].value;
  if (cfg.style.positions.length && !cfg.style.positions.some(p => p.value === next.position)) next.position = cfg.style.positions[0].value;
  // Dismiss defaults appropriate to the surface.
  if (cfg.dismiss.closeButton === "required") next.closeButton = true;
  if (cfg.dismiss.autoDismiss === "default" && (!next.autoDismissSecs || next.autoDismissSecs <= 0)) next.autoDismissSecs = 6;
  if (cfg.dismiss.autoDismiss === "none") next.autoDismissSecs = 0;
  if (!cfg.dismiss.tapBackdrop) next.tapBackdrop = false;
  if (!cfg.dismiss.swipe) next.swipeToDismiss = false;
  return next;
}

// Does this message have at least one working exit path? (BUG-02 publish guard)
export function hasExitPath(style: RuleStyle, type: SurfaceType): boolean {
  const cfg = SURFACE_CONFIG[type];
  if (cfg.dismiss.closeButton === "required") return style.closeButton;
  return style.closeButton
    || (cfg.dismiss.tapBackdrop && style.tapBackdrop)
    || (cfg.dismiss.swipe && style.swipeToDismiss)
    || (cfg.dismiss.autoDismiss !== "none" && style.autoDismissSecs > 0);
}

// R2-28 — a button / product CTA / image tap whose action is Dismiss is a way out too, so a
// message with the close (X) off but a "Not now" button is not flagged. Fullscreen still
// requires its close button (SURFACE_CONFIG), so this does not relax that surface.
export function hasDismissAction(bs: RuleBlock[]): boolean {
  return flattenBlocks(bs || []).some((b) => {
    const c = b.content || {};
    return ((b.type === "button" || b.type === "product") && c.action?.type === "dismiss")
      || (b.type === "image" && c.tapAction?.type === "dismiss");
  });
}
export function messageHasExit(style: RuleStyle, type: SurfaceType, bs: RuleBlock[]): boolean {
  if (SURFACE_CONFIG[type].dismiss.closeButton === "required") return hasExitPath(style, type);
  return hasExitPath(style, type) || hasDismissAction(bs);
}

// ─── Contrast checking (BUG-08) ───────────────────────────────────────────────
// WCAG 2.1 relative-luminance contrast ratio. Handles hex and CSS custom
// properties (var(--brand-primary)) by resolving them off :root at call time.

export function resolveCssColor(c?: string): string {
  if (!c) return "#000000";
  if (c.startsWith("#")) return c;
  if (c.startsWith("var(") && typeof window !== "undefined") {
    const name = c.slice(4, -1).split(",")[0].trim();
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (v) return v.startsWith("#") ? v : `#${v}`;
  }
  return c;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

function relLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const f = (v: number) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

// Returns the WCAG contrast ratio (1–21), or null if a colour can't be parsed.
export function contrastRatio(fg?: string, bg?: string): number | null {
  const a = hexToRgb(resolveCssColor(fg));
  const b = hexToRgb(resolveCssColor(bg));
  if (!a || !b) return null;
  const la = relLuminance(a), lb = relLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// PB-7 (DA-BUG-1421): the nearest shade of `fg` that clears `target` against `bg` — nudged toward white
// (on a dark bg) or black (on a light bg) in 10% steps. Returns a #RRGGBB hex, or null if fg/bg can't be
// parsed. Used to SUGGEST a passing colour instead of hard-blocking a common brand colour.
const toHex = (x: { r: number; g: number; b: number }) => "#" + [x.r, x.g, x.b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("").toUpperCase();
export function nearestPassingShade(fg?: string, bg?: string, target = 4.5): string | null {
  const f = hexToRgb(resolveCssColor(fg));
  const b = hexToRgb(resolveCssColor(bg));
  if (!f || !b) return null;
  if ((contrastRatio(fg, bg) || 0) >= target) return toHex(f); // already passes
  // Move toward whichever extreme actually has more contrast with bg (the white/black crossover is at
  // bg luminance ≈ 0.18, so a mid-tone like orange needs BLACK, not white).
  const toward = (contrastRatio("#000000", resolveCssColor(bg)) || 0) >= (contrastRatio("#FFFFFF", resolveCssColor(bg)) || 0)
    ? { r: 0, g: 0, b: 0 } : { r: 255, g: 255, b: 255 };
  for (let t = 0.1; t <= 1.0001; t += 0.1) {
    const cand = { r: f.r + (toward.r - f.r) * t, g: f.g + (toward.g - f.g) * t, b: f.b + (toward.b - f.b) * t };
    if ((contrastRatio(toHex(cand), bg) || 0) >= target) return toHex(cand);
  }
  return toHex(toward);
}
// " Try #RRGGBB." suffix for a contrast warning, or "" when no better shade can be computed.
function shadeHint(fg?: string, bg?: string, target = 4.5): string {
  const s = nearestPassingShade(fg, bg, target);
  return s ? ` Try ${s}.` : "";
}

// A text block passes if it clears the AA threshold for its size: 3:1 for large
// text (≥18px, or ≥14px bold), else 4.5:1.
export function textBlockContrastFails(block: RuleBlock, background: string): boolean {
  if (block.type !== "heading" && block.type !== "text") return false;
  if (!(block.content.text || "").trim()) return false;
  const ratio = contrastRatio(block.content.color, background);
  if (ratio == null) return false; // can't evaluate (e.g. image background) → don't block
  const size = block.content.fontSize || (block.type === "heading" ? 18 : 14);
  const large = size >= 18 || (size >= 14 && (block.content.fontWeight === "bold" || block.content.fontWeight === "semibold"));
  return ratio < (large ? 3 : 4.5);
}

// ─── Publish gate ─────────────────────────────────────────────────────────────

export interface RuleContext {
  deepLinkSchemes?: string[]; // P-8: In-App settings `deep_link_schemes` ([] = any custom scheme)
  brokenImages?: string[];    // F11: image URLs the browser failed to load (./inAppImageProbe)
  brokenVideos?: string[];    // P7 (DA-BUG-1385): video URLs that failed to load (probeVideos)
  knownVariables?: string[];  // F11: variable names besides DEFAULT_VARIABLES
  now?: Date;                 // P2-7: clock for the ended-schedule check (tests)
  brand?: Brand;              // DA-BUG-1055: tenant brand colours for the dark resolution
}

// The variables the designer offers (R-1 chips). A message may also use the attribute its own
// input blocks save to (`saveToAttribute`).
export const DEFAULT_VARIABLES = ["first_name", "last_name", "account_tier", "city", "email", "phone"];

// Every customer-visible single-string field of a block (F11 variable check, Preview 1 merge tags).
export const TEXT_KEYS = ["text", "label", "placeholder", "submitText", "copyText", "code", "productName", "productDescription", "price", "originalPrice",
  "badge", "iconLabel", "iconSubLabel", "ratingSubLabel", "thankYouMessage", "lowLabel", "highLabel", "link", "expiredText"];

// F11 — every customer-visible string of a block (for the variable check).
export function visibleStrings(b: RuleBlock): { where: string; text: string; field: string }[] {
  const c = b.content || {};
  const name = blockName(b);
  const out: { where: string; text: string; field: string }[] = [];
  const add = (where: string, v: unknown, field: string) => { if (typeof v === "string" && v) out.push({ where, text: v, field }); };
  TEXT_KEYS.forEach((k) => add(name, c[k], k));
  (Array.isArray(c.items) ? c.items : []).forEach((v: unknown) => add(`${name} item`, v, "items"));
  (Array.isArray(c.options) ? c.options : []).forEach((v: unknown) => add(`${name} option`, v, "options"));
  (Array.isArray(c.slides) ? c.slides : []).forEach((sl: any, i: number) => ["title", "description", "cta"].forEach((k) => add(`Carousel slide ${i + 1}`, sl?.[k], `slides.${i}.${k}`)));
  return out;
}

// F11 — image URLs to probe (image, product, carousel slides, video poster).
export function imageUrlsOf(doc: any): string[] {
  const out: string[] = [];
  const bg = doc?.design?.pages?.[0]?.style?.backgroundImage?.url;
  if (typeof bg === "string" && bg.trim()) out.push(bg.trim()); // Gap 13
  pagesOf(doc).forEach((p) => flattenBlocks(p.blocks).forEach((b) => {
    const c = b.content || {};
    if ((b.type === "image" || b.type === "product") && c.imageUrl) out.push(c.imageUrl);
    if (b.type === "video" && c.videoPoster) out.push(c.videoPoster);
    if (b.type === "carousel" && Array.isArray(c.slides)) c.slides.forEach((sl: any) => { if (sl?.imageUrl) out.push(sl.imageUrl); });
  }));
  return Array.from(new Set(out.filter((u) => /^https:\/\//i.test(String(u).trim()))));
}

// P7 (DA-BUG-1385): https video URLs to probe for reachability at publish (the poster is covered by
// imageUrlsOf; the video itself was only format-checked, so a 403/404 video published silently).
export function videoUrlsOf(doc: any): string[] {
  const out: string[] = [];
  pagesOf(doc).forEach((p) => flattenBlocks(p.blocks).forEach((b) => {
    const c = b.content || {};
    if (b.type === "video" && c.videoUrl) out.push(c.videoUrl);
  }));
  return Array.from(new Set(out.filter((u) => /^https:\/\//i.test(String(u).trim()))));
}

// F11 — contrast against the background each block actually sits on: the card, or the nearest
// container / form fill. null = can't be evaluated statically (image or translucent fill) → skip.
const BUTTON_FILL_SECONDARY = "#EDF2F7";
function opaqueFill(c: Record<string, any>, fallback: string | null): string | null {
  if (!c.containerBg) return fallback;
  if ((c.containerBgOpacity ?? 100) < 100) return null;
  return hexToRgb(resolveCssColor(c.containerBg)) ? c.containerBg : null;
}
type AddIssue = (message: string, block?: RuleBlock | null, field?: string | null) => void;
function contrastProblems(bs: RuleBlock[], bg: string | null, add: AddIssue): void {
  bs.forEach((b) => {
    const c = b.content || {};
    if (bg && textBlockContrastFails(b, bg)) add(`Low contrast: "${(c.text || "").slice(0, 18)}…" is hard to read on this background.${shadeHint(c.color, bg)}`, b, "color");
    if (b.type === "button" && (c.text || "").trim()) {
      const bs2 = c.buttonStyle || "primary";
      const fill = bs2 === "primary" ? (c.buttonColor || "var(--brand-primary)") : bs2 === "secondary" ? (c.buttonColor || BUTTON_FILL_SECONDARY) : bg;
      const label = c.buttonTextColor || (bs2 === "primary" ? "#FFFFFF" : bs2 === "secondary" ? (c.buttonColor ? null : "#2D3748") : (c.buttonColor || "var(--brand-primary)"));
      const ratio = fill && label ? contrastRatio(label, fill) : null;
      if (ratio != null && ratio < 3) add(`Low contrast: button "${String(c.text).slice(0, 18)}" label is hard to read on its background.${shadeHint(label || undefined, fill || undefined, 3)}`, b, "buttonTextColor");
    }
    if (b.type === "feature-list" && bg && Array.isArray(c.items) && c.items.length) {
      const ratio = contrastRatio(c.color || "#384150", bg);
      if (ratio != null && ratio < 4.5) add(`Low contrast: feature list text is hard to read on this background.${shadeHint(c.color || "#384150", bg)}`, b, "color");
    }
    // P5 (DA-BUG-1383): countdown digits/label and coupon label/expiry were never contrast-checked —
    // on a dark photo + scrim they were near-black and unreadable with only the generic scrim warning.
    if (b.type === "countdown" && bg) {
      const digits = contrastRatio(c.color || "#1A202C", bg);
      const label = c.label ? contrastRatio(c.labelColor || "#666666", bg) : null;
      if ((digits != null && digits < 4.5) || (label != null && label < 4.5)) add(`Low contrast: countdown text is hard to read on this background.`, b, "color");
    }
    if (b.type === "coupon" && bg) {
      const label = c.label ? contrastRatio(c.labelColor || "#666666", bg) : null;
      const expiry = (c.showExpiry && c.expiryDate) ? contrastRatio(c.expiryColor || "#8C8C8C", bg) : null; // DA-BUG-1421: expiry uses its own colour
      if ((label != null && label < 4.5) || (expiry != null && expiry < 4.5)) add(`Low contrast: coupon label/expiry is hard to read on this background.`, b, "labelColor");
    }
    if (Array.isArray(c.children) && c.children.length) {
      const inner = b.type === "container" || b.type === "form" ? opaqueFill(c, b.type === "form" && !c.containerBg ? "#FFFFFF" : bg) : bg;
      contrastProblems(c.children, inner, add);
    }
  });
}

// P-8 — every URL a block carries, with where it is and which rule applies.
const ACTION_KEYS: [string, string][] = [["action", "action"], ["tapAction", "tap action"], ["afterCopy", "after-copy action"], ["onSubmit", "on-submit action"]];
const blockName = (b: RuleBlock): string => {
  const c = b.content || {};
  const label = ([c.text, c.label, c.productName].find((v) => typeof v === "string" && v.trim()) || "").trim();
  const kind = b.type.charAt(0).toUpperCase() + b.type.slice(1);
  return label ? `${kind} "${label.slice(0, 24)}"` : kind;
};
type UrlRef = { where: string; url: string; kind: "https" | "deeplink"; field: string };
function actionUrls(where: string, act: any, key: string): UrlRef[] {
  if (!act || typeof act !== "object") return [];
  if (act.type === "openUrl") return [{ where: `${where} URL`, url: act.url, kind: "https", field: `${key}.url` }];
  if (act.type === "deepLink") return [{ where: `${where} deep link`, url: act.url, kind: "deeplink", field: `${key}.url` }, { where: `${where} fallback URL`, url: act.fallbackUrl, kind: "https", field: `${key}.fallbackUrl` }];
  return [];
}
export function blockUrls(b: RuleBlock): UrlRef[] {
  const c = b.content || {};
  const name = blockName(b);
  const out: UrlRef[] = [];
  ACTION_KEYS.forEach(([k, label]) => { out.push(...actionUrls(k === "action" ? name : `${name} ${label}`, c[k], k)); });
  if ((b.type === "image" || b.type === "product") && c.imageUrl) out.push({ where: b.type === "image" ? "Image URL" : `${name} image URL`, url: c.imageUrl, kind: "https", field: "imageUrl" });
  if (b.type === "video") {
    out.push({ where: "Video URL", url: c.videoUrl, kind: "https", field: "videoUrl" });
    out.push({ where: "Video poster URL", url: c.videoPoster, kind: "https", field: "videoPoster" });
  }
  if (b.type === "consent") out.push({ where: "Consent link URL", url: c.linkUrl, kind: "https", field: "linkUrl" });
  if (b.type === "carousel" && Array.isArray(c.slides)) {
    c.slides.forEach((sl: any, i: number) => {
      out.push({ where: `Carousel slide ${i + 1} image URL`, url: sl?.imageUrl, kind: "https", field: `slides.${i}.imageUrl` });
      out.push(...actionUrls(`Carousel slide ${i + 1}`, sl?.action, `slides.${i}.action`));
    });
  }
  return out.filter((u) => u.url != null && String(u.url).trim() !== "");
}

const pagesOf = (doc: any): { name: string; blocks: RuleBlock[]; style?: any }[] => {
  const d = (doc && doc.design) || {};
  if (Array.isArray(d.pages) && d.pages.length) {
    return d.pages.map((p: any, i: number) => ({ name: p?.name || `Page ${i + 1}`, blocks: Array.isArray(p?.blocks) ? p.blocks : [], style: p?.style }));
  }
  return [{ name: "Page 1", blocks: Array.isArray(d.blocks) ? d.blocks : [], style: d.style }];
};

export const flattenBlocks = (bs: RuleBlock[]): RuleBlock[] =>
  bs.reduce<RuleBlock[]>((acc, b) => acc.concat(b, b?.content?.children?.length ? flattenBlocks(b.content.children) : []), []);

// The stored style (pages[0].style, snake_case legacy keys tolerated) → RuleStyle.
export function styleFromDoc(doc: any): RuleStyle {
  const st = pagesOf(doc)[0].style || (doc && doc.design && doc.design.style) || {};
  return {
    background: st.background ?? "#FFFFFF",
    animation: st.animation ?? "",
    position: st.position ?? "",
    closeButton: st.closeButton ?? st.close_button ?? true,
    tapBackdrop: st.tapBackdrop ?? st.tap_backdrop ?? false,
    swipeToDismiss: st.swipeToDismiss ?? st.swipe_to_dismiss ?? false,
    autoDismissSecs: st.autoDismissSecs ?? st.auto_dismiss_secs ?? 0,
  };
}

// X-5 — every tappable label a block renders, so none can show as a blank button. A label
// inside a form uses the form's single Submit, so choice/input submit labels count only standalone.
function buttonLabels(bs: RuleBlock[], insideForm = false): { what: string; text: string; block: RuleBlock; field: string }[] {
  const out: { what: string; text: string; block: RuleBlock; field: string }[] = [];
  bs.forEach((b) => {
    const c = b.content || {};
    if (b.type === "button") out.push({ what: "Button label", text: c.text || "", block: b, field: "text" });
    if (b.type === "product") out.push({ what: `${blockName(b)} button`, text: c.text || "", block: b, field: "text" });
    if (b.type === "coupon") out.push({ what: "Coupon copy button", text: c.copyText || "", block: b, field: "copyText" });
    if (b.type === "form") out.push({ what: "Form submit button", text: c.submitText || "", block: b, field: "submitText" });
    if ((b.type === "choice" || b.type === "input") && !insideForm) out.push({ what: `${blockName(b)} submit button`, text: c.submitText || "", block: b, field: "submitText" });
    if (Array.isArray(c.children) && c.children.length) out.push(...buttonLabels(c.children, insideForm || b.type === "form"));
  });
  return out;
}
function labelProblems(bs: RuleBlock[], add: AddIssue): void {
  buttonLabels(bs).forEach(({ what, text, block, field }) => {
    if (!text.trim()) { add(what === "Button label" ? "A button is missing its label." : `${what} is missing its label.`, block, field); return; }
    if (!worstCaseText(text).trim()) {
      const k = mergeTagKeys(text).find((t) => t.key && !t.fallback);
      add(`${what} "${text}" is empty for customers without that value — add a fallback, e.g. {{${k ? k.key : "name"}|Continue}}.`, block, field);
    }
  });
}

// F5 (DA-BUG-1091) — every issue says where it is, so the issues modal can take the author there.
// pageIndex / blockId / field are null for message-level issues. "blocker" stops Publish;
// "warning" is shown but does not.
export interface Issue {
  message: string;
  pageIndex: number | null;
  blockId: string | null;
  field: string | null;
  severity: "blocker" | "warning";
}

// Every issue for this message. BUG-02 / BUG-07 / BUG-08 and every later rule.
export function validateMessageIssues(doc: any, ctx: RuleContext = {}): Issue[] {
  const issues: Issue[] = [];
  let pageAt: number | null = null;
  const add: AddIssue = (message, block = null, field = null) =>
    issues.push({ message, pageIndex: block ? pageAt : null, blockId: block?.id ?? null, field: block ? field ?? null : null, severity: "blocker" });
  const warn: AddIssue = (message, block = null, field = null) =>
    issues.push({ message, pageIndex: block ? pageAt : null, blockId: block?.id ?? null, field: block ? field ?? null : null, severity: "warning" });
  const type: SurfaceType = (SURFACE_CONFIG as any)[doc?.message_type] ? doc.message_type : "modal";
  const style = styleFromDoc(doc);
  const pages = pagesOf(doc);
  // Validate EVERY page (flattened incl. nested layout children), not just the one on screen.
  const allBlocks = pages.reduce<RuleBlock[]>((acc, p) => acc.concat(flattenBlocks(p.blocks)), []);
  if (allBlocks.length === 0) add("Add at least one block.");
  const emptyPage = pages.findIndex(p => p.blocks.length === 0);
  if (pages.length > 1 && emptyPage !== -1) issues.push({ message: `"${pages[emptyPage].name}" has no blocks — add content or remove the page.`, pageIndex: emptyPage, blockId: null, field: null, severity: "blocker" });
  const known = new Set([...DEFAULT_VARIABLES, ...(ctx.knownVariables || []),
    ...allBlocks.map((b) => b.content?.saveToAttribute).filter((k: unknown): k is string => typeof k === "string" && !!k.trim()).map((k) => k.trim())]);
  const bannerSlot: Record<string, [string, number]> = { heading: ["title", BANNER_LIMITS.heading], text: ["body", BANNER_LIMITS.text], button: ["button label", BANNER_LIMITS.button] };
  const insideForm = new Set<string>();
  allBlocks.filter((b) => b.type === "form").forEach((f) => flattenBlocks(f.content?.children || []).forEach((k) => insideForm.add(k.id as string)));
  const ruleCtx: BlockRuleCtx = { type, pages, allBlocks, insideForm, add, warn };
  pages.forEach((page, pi) => {
    pageAt = pi;
    flattenBlocks(page.blocks).forEach((b) => {
      const c = b.content || {};
      if ((b.type === "heading" || b.type === "text") && !(c.text || "").trim()) add(`Empty ${b.type} block.`, b, "text");
      if (b.type === "button") {
        const act = c.action;
        if ((act?.type === "openUrl" || act?.type === "deepLink") && !(act?.url || "").trim()) add("A button action is missing its URL.", b, "action.url");
        if (act?.type === "deepLink" && !(act?.fallbackUrl || "").trim()) add("A deep-link button is missing its fallback URL.", b, "action.fallbackUrl");
        if (act?.type === "navigate" && !(act?.screen || "").trim()) add("A button action is missing its screen.", b, "action.screen");
      }
      if (b.type === "product") { // PB-2 (DA-BUG-1421): the product CTA needs its URL too — was only a generic server error before
        const act = c.action;
        if ((act?.type === "openUrl" || act?.type === "deepLink") && !(act?.url || "").trim()) add("A product card's button needs a URL for its action.", b, "action.url");
        if (act?.type === "deepLink" && !(act?.fallbackUrl || "").trim()) add("A product card's deep-link button is missing its fallback URL.", b, "action.fallbackUrl");
      }
      if (b.type === "image" && c.imageUrl && !(c.imageAlt || "").trim()) add("An image is missing alt text.", b, "imageAlt");
      if (b.type === "countdown") { // R2-16 — each mode needs the value the device reads
        const mode = c.countdownMode || "fixed";
        const nm = blockName(b);
        if (mode === "duration" && !(Number(c.durationSeconds) > 0)) add(`${nm} needs a duration.`, b, "durationSeconds");
        if (mode === "attribute" && !(c.attribute || "").trim()) add(`${nm} needs the attribute that holds its end date.`, b, "attribute");
        if (mode === "fixed" && !(c.endDate || "").trim()) add(`${nm} needs an end date.`, b, "endDate");
      }
      blockUrls(b).forEach(({ where, url, kind, field }) => {
        const p = urlProblem(url, kind, ctx.deepLinkSchemes || []);
        if (p) add(`${where} ${p}.`, b, field);
      });
      // F11 — variables the device can't fill.
      visibleStrings(b).forEach(({ where, text, field }) => {
        mergeTagKeys(text).forEach(({ key }) => {
          if (!key) add(`Empty variable in ${where}: "${text.slice(0, 30)}" — pick a variable or remove the braces.`, b, field);
          // P2 (DA-BUG-1381): a non-built-in key is NOT a blocker — the backend fills ANY custom
          // contact attribute (P-6). Warn (typo hint) instead of blocking a valid custom variable.
          else if (!known.has(key)) warn(`Variable "${key}" in ${where} isn't one of your known attributes — it'll be filled when the contact has it, otherwise it shows blank. Check the spelling if that's unexpected.`, b, field);
        });
      });
      // X-6 — the SDKs never truncate banner copy.
      if (type === "banner") {
        const sl = bannerSlot[b.type];
        const len = (c.text || "").length;
        if (sl && len > sl[1]) add(`Banner ${sl[0]} is ${len} characters — the limit is ${sl[1]}. Shorten it to publish.`, b, "text");
      }
      BLOCK_RULES.forEach((rule) => rule(b, pi, ruleCtx));
    });
    labelProblems(page.blocks, add);
  });
  pageAt = null;
  // P-1 — no default trigger/audience: a fresh draft must say when and to whom it shows.
  if (!(doc?.trigger?.event || "").trim()) add("Choose a trigger (Trigger tab).");
  if (doc?.trigger?.event === "custom_event" && !(doc?.trigger?.custom_event_name || "").trim()) add("Choose the custom event (Trigger tab).");
  // P2-7 — an end date already past: the message would never show again (date = last day shown).
  const sch = doc?.schedule || {};
  if (sch.end_enabled && typeof sch.end_date === "string" && /^\d{4}-\d{2}-\d{2}/.test(sch.end_date)) {
    const end = new Date(sch.end_date.length === 10 ? `${sch.end_date}T23:59:59` : sch.end_date);
    if (!Number.isNaN(end.getTime()) && end.getTime() < (ctx.now || new Date()).getTime()) {
      add(`The schedule ended on ${sch.end_date.slice(0, 10)} — move the end date or turn it off (Schedule tab).`);
    }
  }
  const aud = doc?.audience || {};
  if (!aud.type) add("Choose an audience (Audience tab).");
  else if ((aud.type === "cohorts" || aud.type === "segments") && !(Array.isArray(aud.cohort_ids) && aud.cohort_ids.length)) add("Pick a segment for the audience.");
  else if (aud.type === "tags" && !(Array.isArray(aud.tag_ids) && aud.tag_ids.length)) add("Pick at least one tag for the audience.");
  if (!messageHasExit(style, type, allBlocks)) add(`This ${type} has no way to be dismissed.`);
  // BUG-08 / F11 — contrast. Skip when a background image covers the surface (can't be
  // evaluated statically). Otherwise text must clear WCAG AA against the background it sits on.
  const rawStyle = pages[0].style || (doc && doc.design && doc.design.style) || {};
  // Gap 13 (DA-BUG-1097) — an explicit fullscreen background image + scrim. Text is checked
  // against the scrim colour when the scrim is strong enough to dominate the photo (≥ 60%);
  // a lighter scrim can't be judged statically, so it is a warning.
  const bgSetting = type === "fullscreen" && rawStyle.backgroundImage && typeof rawStyle.backgroundImage.url === "string" && rawStyle.backgroundImage.url.trim()
    ? rawStyle.backgroundImage.url.trim() : null;
  let bgImage = allBlocks.find(b => b.type === "image")?.content?.imageUrl;
  let evaluableBg: string | null = type === "fullscreen" ? (bgImage ? null : style.background) : style.background;
  if (bgSetting) {
    const p = urlProblem(bgSetting, "https");
    if (p) add(`Background image URL ${p}.`);
    const scrim = Math.max(0, Math.min(0.8, Number(rawStyle.scrimOpacity) || 0));
    bgImage = bgSetting;
    if (scrim >= 0.6) evaluableBg = rawStyle.scrimColor || "#000000";
    else {
      evaluableBg = null;
      if (allBlocks.some((b) => (b.type === "heading" || b.type === "text") && (b.content?.text || "").trim())) {
        warn("Text sits on the background image with a light scrim — raise the scrim to 60% or more, or check it on a device (Style tab).");
      }
    }
  }
  // PB-7 (DA-BUG-1421): contrast is a WARNING (shown + suggests a passing shade), not a hard block — a
  // common brand colour (white on #F97316 / #0D9488) should be flagged, not prevented from publishing.
  pages.forEach((p, pi) => { pageAt = pi; contrastProblems(p.blocks, evaluableBg || null, warn); });
  // DA-BUG-1055 — when the message can show dark (Auto or Always dark), run the same contrast
  // checks on the dark resolution the SDKs render (…Dark → authored → D-5 default).
  const appearance = effectiveAppearance(rawStyle.appearance, pages.map((p) => ({ blocks: p.blocks, style: p.style || rawStyle })));
  if (appearance !== "light" && !(type === "fullscreen" && bgImage)) {
    const surface = darkPreviewSurface(rawStyle, ctx.brand || {});
    pages.forEach((p, pi) => {
      pageAt = pi;
      contrastProblems(darkPreviewBlocks(p.blocks, surface, ctx.brand || {}), cssOf(surface), (m, blk, f) => warn(`Dark mode: ${m}`, blk, f));
    });
  }
  pageAt = null;
  // F11 — images that don't load.
  const broken = new Set(ctx.brokenImages || []);
  pages.forEach((p, pi) => {
    pageAt = pi;
    flattenBlocks(p.blocks).forEach((b) => imageUrlsOf({ design: { pages: [{ blocks: [b] }] } })
      .filter((u) => broken.has(u)).forEach((u) => add(`Image URL can't be loaded: ${u}`, b, "imageUrl")));
  });
  // P7 (DA-BUG-1385): videos that don't load (reachability), mirroring the image check above.
  const brokenVid = new Set(ctx.brokenVideos || []);
  if (brokenVid.size) pages.forEach((p, pi) => {
    pageAt = pi;
    flattenBlocks(p.blocks).forEach((b) => {
      const u = (b.content || {}).videoUrl;
      if (b.type === "video" && typeof u === "string" && brokenVid.has(u)) add(`Video URL can't be loaded: ${u}`, b, "videoUrl");
    });
  });
  return issues;
}

// Per-block rules added by later items (open-items batch). Each gets the block, its page and the
// message context, and reports through add / warn.
export interface BlockRuleCtx { type: SurfaceType; pages: { name: string; blocks: RuleBlock[]; style?: any }[]; allBlocks: RuleBlock[]; insideForm: Set<string>; add: AddIssue; warn: AddIssue }
type BlockRule = (b: RuleBlock, pageIndex: number, ctx: BlockRuleCtx) => void;
// Every action slot a block carries (field = its key; slides as slides.<i>.action).
export function actionSlots(b: RuleBlock): { field: string; action: any }[] {
  const c = b.content || {};
  const out: { field: string; action: any }[] = [];
  ["action", "tapAction", "afterCopy", "onSubmit"].forEach((k) => { if (c[k] && typeof c[k] === "object") out.push({ field: k, action: c[k] }); });
  (Array.isArray(c.slides) ? c.slides : []).forEach((sl: any, i: number) => { if (sl?.action) out.push({ field: `slides.${i}.action`, action: sl.action }); });
  return out;
}
// Gap 1 (DA-BUG-1092) — previousPage on the first page and nextPage on the last page do nothing.
const pageMoveRule: BlockRule = (b, pi, ctx) => {
  actionSlots(b).forEach(({ field, action }) => {
    if (action.type === "previousPage" && pi === 0) ctx.add(`${blockName(b)} goes to the previous page, but this is the first page.`, b, field);
    if (action.type === "nextPage" && pi === ctx.pages.length - 1) ctx.add(`${blockName(b)} goes to the next page, but this is the last page.`, b, field);
  });
};
// Gap 5 (DA-BUG-1094) — copyCoupon copies a coupon block's code (`couponBlockId`; absent = the first).
const copyCouponRule: BlockRule = (b, _pi, ctx) => {
  const coupons = ctx.allBlocks.filter((x) => x.type === "coupon");
  actionSlots(b).forEach(({ field, action }) => {
    if (action.type !== "copyCoupon") return;
    if (!coupons.length) ctx.add(`${blockName(b)} copies a coupon, but the message has no coupon block.`, b, field);
    else if (action.couponBlockId && !coupons.some((x) => x.id === action.couponBlockId)) ctx.add(`${blockName(b)} copies a coupon that is no longer in the message — pick one.`, b, field);
  });
};
// Gap 7 (DA-BUG-1099) — an emitEvent action without an event name sends nothing.
const SLOT_LABEL: Record<string, string> = { action: "", tapAction: " tap action", afterCopy: " after-copy action", onSubmit: " on-submit action" };
const emitEventRule: BlockRule = (b, _pi, ctx) => {
  actionSlots(b).forEach(({ field, action }) => {
    if (action.type !== "emitEvent" || (action.event || "").trim()) return;
    const where = field.startsWith("slides.") ? `Carousel slide ${Number(field.split(".")[1]) + 1}` : `${b.type === "image" ? "Image" : blockName(b)}${SLOT_LABEL[field] ?? ""}`;
    ctx.add(`${where}${field === "action" ? " action" : ""} emits an event but has no event name.`, b, `${field}.event`);
  });
};
// Gap 8 (DA-BUG-1100) — choice options unique and non-empty; attribute keys [a-z0-9_]; a field
// key only means something inside a Form.
const ATTRIBUTE_KEY = /^[a-z0-9_]+$/;
const dataFieldRule: BlockRule = (b, _pi, ctx) => {
  const c = b.content || {};
  if (b.type === "choice" && Array.isArray(c.options)) {
    const seen = new Map<string, string>();
    let empty = false;
    c.options.forEach((o: unknown) => {
      const v = String(o ?? "").trim();
      if (!v) { empty = true; return; }
      const k = v.toLowerCase();
      if (seen.has(k)) ctx.add(`${blockName(b)} has the option "${seen.get(k)}" more than once.`, b, "options");
      else seen.set(k, v);
    });
    if (empty) ctx.add(`${blockName(b)} has an empty option.`, b, "options");
  }
  if (typeof c.saveToAttribute === "string" && c.saveToAttribute.trim() && !ATTRIBUTE_KEY.test(c.saveToAttribute.trim())) {
    ctx.add(`${blockName(b)} saves to "${c.saveToAttribute.trim()}" — attribute keys use lower-case letters, digits and _ only.`, b, "saveToAttribute");
  }
  if ((b.type === "input" || b.type === "choice" || b.type === "consent") && typeof c.fieldKey === "string" && c.fieldKey.trim() && !ctx.insideForm.has(b.id as string)) {
    ctx.warn(`${blockName(b)} has a field key ("${c.fieldKey.trim()}") but is not inside a Form, so it is not used.`, b, "fieldKey");
  }
};
// Gap 9 (DA-BUG-1101) — consent must be given by the customer; a link needs a URL.
const consentRule: BlockRule = (b, _pi, ctx) => {
  if (b.type !== "consent") return;
  const c = b.content || {};
  if (c.required && c.preChecked) ctx.warn("Consent is required and pre-checked — consent should be given by the customer, so leave it unchecked.", b, "preChecked");
  if ((c.link || "").trim() && !(c.linkUrl || "").trim()) ctx.add(`Consent link "${c.link.trim()}" has no URL — add one or clear the link text.`, b, "linkUrl");
};
// Gap 6 (DA-BUG-1102) — a carousel with no slide renders a fake "Slide" placeholder.
const carouselRule: BlockRule = (b, _pi, ctx) => {
  if (b.type === "carousel" && !(Array.isArray(b.content?.slides) && b.content.slides.length)) ctx.add("Carousel needs at least one slide.", b, "slides");
};
// Gap 4 (DA-BUG-1103) — an empty icon used to become 🔔 silently; long icon text overflows the badge.
export const ICON_TEXT_MAX = 4;
const iconRule: BlockRule = (b, _pi, ctx) => {
  if (b.type !== "icon") return;
  const v = String(b.content?.iconValue ?? "").trim();
  if (!v) ctx.add("Icon has no icon or text — pick one or remove the block.", b, "iconValue");
  else if (Array.from(v).length > ICON_TEXT_MAX) ctx.add(`Icon text is ${Array.from(v).length} characters — keep it to ${ICON_TEXT_MAX} so it fits the badge.`, b, "iconValue");
};
// Gap 3 (DA-BUG-1104) — columns narrower than ~64 pt on a phone break words; warn.
export const MIN_COLUMN_PT = 64;
const narrowColumnsRule: BlockRule = (b, _pi, ctx) => {
  if (b.type !== "columns") return;
  const c = b.content || {};
  const n = Array.isArray(c.columns) && c.columns.length ? c.columns.length : (Number(c.columnCount) || 2);
  const style = ctx.pages[0]?.style || {};
  const pct = ctx.type === "fullscreen" || ctx.type === "banner" ? 100 : Math.max(40, Math.min(100, Number(style.width) || (ctx.type === "slide-in" ? 60 : 90)));
  const card = 375 * pct / 100 - 32 - 2 * (Number(c.containerPadding) || 0);
  const gap = Number.isFinite(Number(c.gap)) ? Number(c.gap) : 12;
  const col = (card - (n - 1) * gap) / n;
  if (col < MIN_COLUMN_PT) ctx.warn(`Columns are about ${Math.max(0, Math.round(col))} pt wide on a phone — text will wrap badly. Use fewer columns or a wider message.`, b, "columnCount");
};
const BLOCK_RULES: BlockRule[] = [pageMoveRule, copyCouponRule, emitEventRule, dataFieldRule, consentRule, carouselRule, iconRule, narrowColumnsRule];

// The blocking messages only ([] = publishable). Kept for callers that gate on strings.
export function validateMessageDoc(doc: any, ctx: RuleContext = {}): string[] {
  return validateMessageIssues(doc, ctx).filter((i) => i.severity === "blocker").map((i) => i.message);
}

// P-1 — publishing to "All customers" reaches every customer on their next trigger; the caller
// asks for an explicit confirmation first (editor and list alike).
export const targetsAllCustomers = (doc: any): boolean => doc?.audience?.type === "all";
export const ALL_CUSTOMERS_CONFIRM =
  "This message targets ALL customers: every customer who meets the trigger will see it. Publish to all customers?";
